/**
 * Sticky commerce-bar summary derived from Retailer Price Module v1.
 * Does not query offers, classify freshness, or re-rank retailers.
 */

export const STICKY_PRICE_BAR_HREF = '#current-prices';
export const STICKY_MOBILE_MEDIA_QUERY = '(max-width: 767px)';
export const STICKY_PRICES_ROOT_MARGIN = '0px 0px 72px 0px';

export function isPastStickySentinel(entry) {
  if (!entry) return false;
  const top = entry.boundingClientRect?.top;
  return Boolean(entry.isIntersecting) || (typeof top === 'number' && top < 0);
}

export function hasReachedComparisonStart(entryOrTop, viewportHeight) {
  if (typeof entryOrTop === 'number') {
    return entryOrTop <= 8;
  }
  const top = entryOrTop?.boundingClientRect?.top;
  if (typeof top === 'number') return top <= 8;
  if (typeof viewportHeight === 'number') {
    return Boolean(entryOrTop?.isIntersecting) && viewportHeight > 0;
  }
  return false;
}

export function hasReachedRetailerModule(entryOrTop, viewportHeight) {
  if (typeof entryOrTop === 'number') {
    const height = typeof viewportHeight === 'number' ? viewportHeight : 0;
    return entryOrTop < height + 72;
  }
  return isPastStickySentinel(entryOrTop);
}

export function getStickyPriceBarSummary(module, options = {}) {
  const productLabel = options.productLabel || 'LVS42HD';
  const visible = Array.isArray(module?.visibleRetailers) ? module.visibleRetailers : [];
  if (!module || visible.length === 0) {
    return {
      shouldShow: false,
      hasCurrent: false,
      price: null,
      priceLabel: null,
      fallbackLabel: null,
      actionLabel: null,
      ariaLabel: null,
      href: STICKY_PRICE_BAR_HREF,
      currentLowestRetailerCount: 0,
      currentRetailerCount: 0,
      destinationCount: 0,
    };
  }

  const currentRetailerCount =
    (module.primaryRetailers?.length || 0) + (module.otherCurrentRetailers?.length || 0);
  const currentLowestRetailerCount = module.hasCurrent ? module.primaryCount || 0 : 0;

  let actionLabel = 'See retailers';
  let ariaLabel = `See current ${productLabel} prices`;
  if (module.hasCurrent && currentLowestRetailerCount >= 2) {
    actionLabel = `See ${currentLowestRetailerCount} retailers`;
    ariaLabel = `See current ${productLabel} prices at ${currentLowestRetailerCount} retailers`;
  } else if (
    module.hasCurrent &&
    currentLowestRetailerCount === 1 &&
    (module.otherCurrentRetailers?.length || 0) > 0
  ) {
    actionLabel = 'See retailers';
    ariaLabel = `See current ${productLabel} prices`;
  } else if (currentRetailerCount === 1 || visible.length === 1) {
    actionLabel = 'See retailer';
    ariaLabel = `See current ${productLabel} prices`;
  }

  return {
    shouldShow: true,
    hasCurrent: Boolean(module.hasCurrent),
    price: module.hasCurrent ? module.lowestPrice : null,
    priceLabel: module.hasCurrent ? module.lowestPriceLabel : null,
    fallbackLabel: module.hasCurrent ? null : 'Check current prices',
    actionLabel,
    ariaLabel,
    href: STICKY_PRICE_BAR_HREF,
    currentLowestRetailerCount,
    currentRetailerCount,
    destinationCount: visible.length,
  };
}
